import { createContext, useContext, useState } from 'react';

// Subjects (#247): the parent picks a child's subjects on the Profile tab, from popular homeschool subjects plus
// their own. Every Subject field on the child's pages then offers that list (through SubjectsContext).

export const POPULAR_SUBJECTS = [
  'Math',
  'Reading',
  'Writing',
  'Language Arts',
  'Spelling',
  'Grammar',
  'Phonics',
  'Handwriting',
  'Science',
  'History',
  'Geography',
  'Social Studies',
  'Bible',
  'Art',
  'Music',
  'Physical Education',
  'Health',
  'Spanish',
  'Latin',
  'Computer Science',
  'Logic',
  'Life Skills',
];

/** The current child's subjects; ChildPage provides it. */
export const SubjectsContext = createContext<string[]>([]);

const OTHER = '\u0000other';
const same = (a: string, b: string) => a.toLowerCase() === b.toLowerCase();

/**
 * A Subject field: a dropdown of the child's subjects (plus `extra`, e.g. subjects already in the grade book) with
 * "Other…" for a one-off. With no subjects picked yet it's a text box that suggests the popular ones.
 */
export function SubjectField({ value, onChange, extra = [], placeholder = 'e.g. Math' }: { value: string; onChange: (v: string) => void; extra?: string[]; placeholder?: string }) {
  const picked = useContext(SubjectsContext);
  const options = [...picked, ...extra]
    .filter((s, i, all) => s && all.findIndex((t) => same(t, s)) === i)
    .sort((a, b) => a.localeCompare(b));
  const match = options.find((o) => same(o, value));
  const [custom, setCustom] = useState(() => !!value.trim() && !match);

  if (!options.length) {
    return (
      <>
        <input value={value} onChange={(e) => onChange(e.target.value)} maxLength={60} list="cg-popular-subjects" placeholder={placeholder} />
        <datalist id="cg-popular-subjects">
          {POPULAR_SUBJECTS.map((s) => (
            <option key={s} value={s} />
          ))}
        </datalist>
      </>
    );
  }
  if (custom) {
    return (
      <span className="subject-custom">
        <input value={value} onChange={(e) => onChange(e.target.value)} maxLength={60} placeholder="Type a subject" autoFocus />
        <button
          type="button"
          className="btn small ghost"
          onClick={(e) => {
            e.preventDefault();
            setCustom(false);
            onChange(match ?? '');
          }}
        >
          Pick from list
        </button>
      </span>
    );
  }
  return (
    <select
      value={match ?? ''}
      onChange={(e) => {
        if (e.target.value === OTHER) {
          setCustom(true);
          onChange('');
        } else onChange(e.target.value);
      }}
    >
      <option value="">No subject</option>
      {options.map((s) => (
        <option key={s} value={s}>
          {s}
        </option>
      ))}
      <option value={OTHER}>Other…</option>
    </select>
  );
}
